<template>
    <div>
        <div class="flex items-start mb-16">
            <select-dropdown v-model="form.type"
                             static-id="interviewInvitationType"
                             :options="types"
                             reference="name"
                             label="translation"
                             :class="$slots.default  ? 'w-1/2 mr-32' : 'w-full'"
                             :auto-translate="false"
                             :placeholder="$t('interview.type')"
            >
                {{ $t('interview.type') }}
            </select-dropdown>
            <slot/>
        </div>

        <div class="flex items-start mb-16">
            <template v-if="form.type === 'location'">
                <select-dropdown v-model="form.company_location_id"
                                 :auto-translate="false"
                                 :options="locationOptions"
                                 class="mr-32 w-1/2">
                    {{ getTypeInputLabel }}
                </select-dropdown>
            </template>

            <template v-if="form.type === 'location' && companyLocation">
                <simple-input class="w-1/2"
                              :disabled="true"
                              :value="companyLocation.street">
                    {{ $t('applications.street') }}
                </simple-input>
            </template>

            <template v-if="form.type === 'location' && !companyLocation">
                <simple-input class="w-1/2"
                              v-model="form.street">
                    {{ $t('applications.street') }}
                </simple-input>
            </template>

            <simple-input
                v-else-if="typeIsOnline"
                v-model="form.meeting_url"
                :errors="form.errors.meeting_url"
                class="w-full"
            >
                {{ getTypeInputLabel }}
            </simple-input>
        </div>

        <template v-if="form.type === 'location' && companyLocation">
            <div class="flex items-start mb-16">
                <simple-input class="w-1/2 mr-32"
                              :disabled="true"
                              :value="companyLocation.postal_code">
                    {{ $t('applications.postal_code') }}
                </simple-input>
                <simple-input class="w-1/2"
                              :disabled="true"
                              :value="companyLocation.city">
                    {{ $t('applications.city') }}
                </simple-input>
            </div>
            <div class="flex items-start mb-16">
                <select-dropdown :options="countries"
                                 :placeholder="$t('auth.choose_country')"
                                 class="w-full"
                                 reference="id"
                                 label="name"
                                 :auto-translate="false"
                                 :disabled="true"
                                 :value="getCountryIdByCode(companyLocation.country_code)">
                    {{ $t('applications.country') }}
                </select-dropdown>
            </div>
        </template>

        <template v-if="form.type === 'location' && !companyLocation">
            <div class="flex items-start mb-16">
                <simple-input class="w-1/2 mr-32"
                              v-model="form.postal_code">
                    {{ $t('applications.postal_code') }}
                </simple-input>
                <simple-input class="w-1/2"
                              v-model="form.city">
                    {{ $t('applications.city') }}
                </simple-input>
            </div>
            <div class="flex items-start mb-16">
                <select-dropdown :options="countries"
                                 :placeholder="$t('auth.choose_country')"
                                 class="w-full"
                                 reference="id"
                                 label="name"
                                 :auto-translate="false"
                                 v-model="form.country_id">
                    {{ $t('applications.country') }}
                </select-dropdown>
            </div>
        </template>

        <div class="mb-16 pt-24 pl-24 bg-primary rounded overflow-hidden" v-if="getOnlineURL">
            <div class="mr-24 mb-24 flex">
                <div class="mr-24">
                    <a :href="getOnlineURL" target="_blank" class="h-40 flex items-center justify-center">
                        <interview-icons :type="form.type"/>
                    </a>
                </div>
                <div class="text-body-sm text-white">
                    {{ $t(`interview.${ form.type }_hint`) }}
                </div>
            </div>
        </div>

        <div class="mt-32 flex justify-end">
            <simple-button
                v-if="canManage"
                :loading="saving"
                display="secondary"
                look="primary"
                @click="saveType"
            >
                {{ $t('general.confirm') }}
            </simple-button>
        </div>
    </div>
</template>
<script>
    import SelectDropdown from "./forms/SelectDropdown";
    import Input from "./forms/Input";
    import InterviewIcons from "./InterviewIcons";
    import Form from "../etc/forms/Form";
    import Location from "../models/Location";
    import {buildDropdownOptions, normalizeJson} from "../../shared/helpers";
    import * as locations from "../services/LocationService";
    import * as countryService from "../services/CountryService";
    import * as interviewService from "../services/InterviewService";
    import Country from "../models/Country";
    import SimpleButton from "./ui/SimpleButton";
    import {mapState} from 'pinia';
    import {useRepo} from 'pinia-orm';
    import {useMainStore} from '../state/store';

    export default {
        components: {
            InterviewIcons,
            'simple-input': Input,
            SelectDropdown,
            SimpleButton,
        },
        props: {
            value: Form,
            canManage: {type: Boolean, default: false},
        },
        data() {
            return {
                saving: false,
                types: null,
            }
        },
        computed: {
            ...mapState(useMainStore, ['locale']),
            form: {
                get() {
                    return this.value
                },
                set(form) {
                    this.$emit('input', form);
                }
            },

            locations() {
                return useRepo(Location).all();
            },
            locationOptions() {
                let locations = this.locations.map(location => ({
                    label: location.alias[this.locale] || `${location.street}, ${location.postal_code} ${location.city}`,
                    value: location.id
                }))

                locations.unshift({
                    label: `--- ${this.$t('general.other')} ---`,
                    value: null
                })

                return locations;
            },
            getTypeInputLabel() {
                if (this.form.type === "location")
                    return this.$t('interview.location')
                if (this.typeIsOnline)
                    return this.$t('interview.meeting_url')

                return null;
            },
            typeIsOnline() {
                return ['zoom', 'google', 'ms-teams', 'online'].includes(this.form.type);
            },
            getOnlineURL() {
                switch (this.form.type) {
                    case "zoom":
                        return "https://zoom.us/meeting/schedule";

                    case "google":
                        return "https://meet.google.com";

                    case "ms-teams":
                        return "https://www.microsoft.com/nl-be/microsoft-365/microsoft-teams/group-chat-software"

                    default:
                        return null;
                }
            },
            companyLocation() {
                if (!this.form.company_location_id)
                    return null;

                return this.locations.find(i => parseInt(i.id) === parseInt(this.form.company_location_id));
            },
            countries() {
                return useRepo(Country).orderBy('name').get();
            },
        },
        methods: {
            fetch() {
                this.fetchLocations();
                this.fetchCountries();
                this.fetchInterviewTypes();
            },
            fetchCountries() {
                countryService.index().then((result) => useRepo(Country).save(result));
            },
            fetchInterviewTypes() {
                interviewService.types().then(result => {
                    // TODO: receive array instead of object from backend

                    let arr = [];
                    for (const [key, value] of Object.entries(result)) {
                        arr.push(value);
                    }
                    this.types = arr;
                });
            },
            fetchLocations() {
                locations.index().then(result => {
                    return useRepo(Location).save(normalizeJson(result.data));
                });
            },
            getCountryIdByCode(code) {
                return useRepo(Country).where('code', code.toLowerCase()).first()?.id;
            },
            saveType() {
                this.saving = true;
                this.$emit('saved');
            }
        },
        mounted() {
            this.fetch();
        },
        beforeUnmount() {
            if (!this.saving) {
                this.form.reset('type', this.form.original.type);
            }
            this.saving = false;
        }
    }
</script>
